import React, { useState, useRef, useEffect } from 'react';
import { useAuth } from './AuthProvider.js';
/**
 * Static registry of known apps — extend as new apps are added.
 * `icon` should be an absolute URL (the switcher renders on satellite-app origins
 * where a relative /app-icons path would 404).
 * `url` (optional) overrides the default `https://{appId}.{baseDomain}` link.
 * `builtIn: true` renders the app for everyone, without an `allowedApps` claim
 * (for Hub-internal apps like Origin that aren't SSO satellites).
 */
const APP_REGISTRY = {
    forecast: { name: 'Apex', icon: 'https://fhl.one/app-icons/apex.png', description: '2026 Forecast Dashboard' },
    launchpad: { name: 'LaunchPad', icon: 'https://fhl.one/app-icons/launchpad.png', description: 'All Your Training' },
    origin: {
        name: 'Origin',
        icon: 'https://fhl.one/app-icons/origin.png',
        description: 'Automated Past-Client Follow-Up',
        url: 'https://fhl.one/crm',
        builtIn: true,
    },
};
export const AppSwitcher = ({ currentAppId, baseDomain = 'fhl.one', mainAppUrl = 'https://fhl.one', position = 'bottom-right', buttonStyle, apps: appsProp, iconColor, colorMode = 'light', }) => {
    // Always call useAuth (rules of hooks), but it returns defaults when outside AuthProvider
    const auth = useAuth();
    const rawApps = appsProp !== null && appsProp !== void 0 ? appsProp : (auth.allowedApps || []);
    const [isOpen, setIsOpen] = useState(false);
    const menuRef = useRef(null);
    const isDark = colorMode === 'dark';
    const theme = {
        btnOpenBg: isDark ? 'rgba(255, 255, 255, 0.15)' : 'rgba(31, 78, 121, 0.1)',
        btnHoverBg: isDark ? 'rgba(255, 255, 255, 0.1)' : 'rgba(31, 78, 121, 0.06)',
        iconColorDefault: isDark ? '#f3f4f6' : '#1F4E79',
        menuBg: isDark ? '#1f2937' : 'white',
        menuBorder: isDark ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.08)',
        headerBorder: isDark ? '#374151' : '#f0f0f0',
        linkHoverBg: isDark ? '#374151' : '#f8fafc',
        linkText: isDark ? '#f3f4f6' : '#1f2937',
        linkSubtext: '#9ca3af',
        mainAppBorderBottom: isDark ? '#374151' : '#f5f5f5',
        currentAppBg: isDark ? '#374151' : '#eff6ff',
        currentAppText: isDark ? '#60a5fa' : '#1F4E79',
        iconBoxBg: isDark ? '#4b5563' : '#f1f5f9',
    };
    const finalIconColor = iconColor || theme.iconColorDefault;
    // Close menu when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (menuRef.current && !menuRef.current.contains(event.target)) {
                setIsOpen(false);
            }
        };
        if (isOpen) {
            document.addEventListener('mousedown', handleClickOutside);
        }
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [isOpen]);
    // Close on Escape
    useEffect(() => {
        const handleEscape = (event) => {
            if (event.key === 'Escape')
                setIsOpen(false);
        };
        if (isOpen) {
            document.addEventListener('keydown', handleEscape);
        }
        return () => document.removeEventListener('keydown', handleEscape);
    }, [isOpen]);
    // Claim-gated apps first, then built-in apps (always shown) last.
    const builtInIds = Object.keys(APP_REGISTRY).filter((id) => APP_REGISTRY[id].builtIn);
    const apps = [...rawApps.filter((id) => !builtInIds.includes(id)), ...builtInIds];
    // Don't render if no apps to show
    if (apps.length === 0 && !mainAppUrl)
        return null;
    const menuPosition = position === 'bottom-left'
        ? { left: 0, top: '100%', marginTop: '0.5rem' }
        : { right: 0, top: '100%', marginTop: '0.5rem' };
    return (React.createElement("div", { ref: menuRef, style: { position: 'relative', display: 'inline-block' } },
        React.createElement("button", { onClick: () => setIsOpen(!isOpen), "aria-label": "App switcher", "aria-expanded": isOpen, style: {
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: '2.25rem',
                height: '2.25rem',
                borderRadius: '0.5rem',
                border: 'none',
                backgroundColor: isOpen ? theme.btnOpenBg : 'transparent',
                cursor: 'pointer',
                transition: 'all 0.15s ease',
                padding: 0,
                ...buttonStyle,
            }, onMouseOver: (e) => {
                if (!isOpen)
                    e.currentTarget.style.backgroundColor = theme.btnHoverBg;
            }, onMouseOut: (e) => {
                if (!isOpen)
                    e.currentTarget.style.backgroundColor = 'transparent';
            } },
            React.createElement("svg", { width: "20", height: "20", viewBox: "0 0 24 24", fill: "none", stroke: "currentColor", strokeWidth: "2", strokeLinecap: "round", strokeLinejoin: "round", style: { color: finalIconColor } },
                React.createElement("circle", { cx: "12", cy: "5", r: "1" }),
                React.createElement("circle", { cx: "19", cy: "5", r: "1" }),
                React.createElement("circle", { cx: "5", cy: "5", r: "1" }),
                React.createElement("circle", { cx: "12", cy: "12", r: "1" }),
                React.createElement("circle", { cx: "19", cy: "12", r: "1" }),
                React.createElement("circle", { cx: "5", cy: "12", r: "1" }),
                React.createElement("circle", { cx: "12", cy: "19", r: "1" }),
                React.createElement("circle", { cx: "19", cy: "19", r: "1" }),
                React.createElement("circle", { cx: "5", cy: "19", r: "1" }))),
        isOpen && (React.createElement("div", { style: {
                position: 'absolute',
                ...menuPosition,
                minWidth: '280px',
                backgroundColor: theme.menuBg,
                borderRadius: '0.75rem',
                boxShadow: '0 10px 25px -3px rgba(0, 0, 0, 0.15), 0 4px 6px -2px rgba(0, 0, 0, 0.08)',
                border: `1px solid ${theme.menuBorder}`,
                zIndex: 9999,
                overflow: 'hidden',
                animation: 'fhl-menu-enter 0.15s ease-out',
            } },
            React.createElement("div", { style: {
                    padding: '0.75rem 1rem 0.5rem',
                    borderBottom: `1px solid ${theme.headerBorder}`,
                } },
                React.createElement("span", { style: {
                        fontSize: '0.65rem',
                        fontWeight: 700,
                        textTransform: 'uppercase',
                        letterSpacing: '0.05em',
                        color: theme.linkSubtext,
                        fontFamily: 'system-ui, -apple-system, sans-serif',
                    } }, "Apps")),
            React.createElement("div", { style: {
                    display: 'grid',
                    gridTemplateColumns: 'repeat(3, 1fr)',
                    gap: '0.5rem',
                    padding: '1rem',
                } },
                React.createElement("a", { href: mainAppUrl, style: {
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '0.5rem',
                        padding: '0.75rem 0.25rem',
                        minWidth: 0,
                        textDecoration: 'none',
                        color: theme.linkText,
                        borderRadius: '0.5rem',
                        transition: 'background-color 0.1s',
                        fontFamily: 'system-ui, -apple-system, sans-serif',
                    }, onMouseOver: (e) => { e.currentTarget.style.backgroundColor = theme.linkHoverBg; }, onMouseOut: (e) => { e.currentTarget.style.backgroundColor = 'transparent'; } },
                    React.createElement("span", { style: {
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            width: '3.5rem',
                            height: '3.5rem',
                            fontSize: '1.5rem',
                            flexShrink: 0,
                        } },
                        React.createElement("svg", { width: "30", height: "30", viewBox: "0 0 24 24", fill: "none", stroke: isDark ? '#f3f4f6' : '#1F4E79', strokeWidth: "2", strokeLinecap: "round", strokeLinejoin: "round" },
                            React.createElement("path", { d: "M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" }),
                            React.createElement("polyline", { points: "9 22 9 12 15 12 15 22" }))),
                    React.createElement("div", { style: { fontSize: '0.75rem', fontWeight: 500, textAlign: 'center', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: '100%', lineHeight: 1.1 } }, "Hub Main")),
                apps.map(appId => {
                    var _a;
                    const meta = APP_REGISTRY[appId] || { name: appId, icon: '📱' };
                    const url = (_a = meta.url) !== null && _a !== void 0 ? _a : `https://${appId}.${baseDomain}`;
                    const isCurrentApp = appId === currentAppId;
                    // Treat the icon as an image when it's a full URL or an absolute path; otherwise it's an emoji.
                    const isImageIcon = /^(https?:\/\/|\/)/.test(meta.icon);
                    return (React.createElement("a", { key: appId, href: url, style: {
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: '0.5rem',
                            padding: '0.75rem 0.25rem',
                            minWidth: 0,
                            textDecoration: 'none',
                            color: isCurrentApp ? theme.currentAppText : theme.linkText,
                            backgroundColor: isCurrentApp ? theme.currentAppBg : 'transparent',
                            borderRadius: '0.5rem',
                            transition: 'background-color 0.1s',
                            fontFamily: 'system-ui, -apple-system, sans-serif',
                        }, onMouseOver: (e) => { if (!isCurrentApp)
                            e.currentTarget.style.backgroundColor = theme.linkHoverBg; }, onMouseOut: (e) => { if (!isCurrentApp)
                            e.currentTarget.style.backgroundColor = isCurrentApp ? theme.currentAppBg : 'transparent'; } },
                        React.createElement("span", { style: {
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                width: '3.5rem',
                                height: '3.5rem',
                                fontSize: '2rem',
                                flexShrink: 0,
                            } }, isImageIcon ? (React.createElement("img", { src: meta.icon, alt: `${meta.name} icon`, style: { width: '2.75rem', height: '2.75rem', objectFit: 'contain', borderRadius: '0.5rem' } })) : (meta.icon)),
                        React.createElement("div", { style: { fontSize: '0.75rem', fontWeight: 500, textAlign: 'center', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: '100%', lineHeight: 1.1 } }, meta.name)));
                })),
            React.createElement("style", null, `
            @keyframes fhl-menu-enter {
              from { opacity: 0; transform: translateY(-4px); }
              to { opacity: 1; transform: translateY(0); }
            }
          `)))));
};
